import { BucketNodeDataSchema } from '@cotera/api';
import { WORKFLOW_TRIGGER_UPSTREAM_SOURCE_ID } from '@cotera/utilities';
import type { WorkflowNodeData } from '../types';
import type { WorkflowNodeHydration } from '../workflow-node-base';
import type { WorkflowNodeOutputShapeContext } from '../workflow-node-output-shape';
import { parseBucketConfig } from './bucket-flush-policy';
import {
  displayBucketFlushOutputShape,
  inferShapeFromSample,
} from './output-shape-helpers';
import { TypedWorkflowNode } from './typed-workflow-node';

/**
 * A bucket node accumulates the output of many runs of the same workflow. Each
 * run that reaches it is recorded and absorbed (the branch stops there); when the
 * node's flush policy fires — e.g. "every 500 items or every day" — a single
 * downstream run executes with the combined data.
 *
 * Unlike a filter or debounce, a bucket does not pass its input through: its
 * output is the flush envelope (`{ items, count, reduced?, window }`), not its
 * predecessor's shape. It derives exactly that envelope here (mirroring
 * `buildBucketFlushEnvelope`) so the field pickers on the steps below it — a
 * group-by's "list to split", say — can offer `/items`, `/count` and `/window`
 * rather than a bare "Previous step" with no fields to drill into.
 *
 * `items` carries the predecessor's shape as its element type when the bucket
 * stores whole contributions (the default); an `accumulate` pointer that selects
 * a sub-field leaves the element generic. When the bucket is an entry node (fed
 * by a trigger rather than another node) there is no predecessor shape, so the
 * element is inferred from the trigger's sample instead — otherwise a group-by
 * below a `dataset -> bucket` chain would see no fields to group on.
 */
export class BucketWorkflowNode extends TypedWorkflowNode<
  typeof BucketNodeDataSchema
> {
  protected readonly schema = BucketNodeDataSchema;

  static override create(
    data: WorkflowNodeData,
    hydration: WorkflowNodeHydration
  ): BucketWorkflowNode {
    return new BucketWorkflowNode(data, hydration);
  }

  override deriveTypedOutputShape(
    context: WorkflowNodeOutputShapeContext
  ): string | null {
    const parsed = parseBucketConfig(this._data.data);
    const reducerKeys = parsed.ok
      ? (parsed.config.reducers ?? []).map((reducer) => reducer.as)
      : [];
    // The whole contribution is stored unless `accumulate` selects a sub-field,
    // so only in that default case is each item's shape its input's.
    const storesWholeItem = parsed.ok && parsed.config.accumulate === undefined;
    const itemShape = storesWholeItem ? this.resolveItemShape(context) : null;
    return displayBucketFlushOutputShape({ itemShape, reducerKeys });
  }

  /**
   * The shape of one stored item: the predecessor's output when a node feeds the
   * bucket, or — for an entry bucket fed by a trigger — the shape inferred from
   * the trigger's sample (the trigger has a sample but no shape resolver). The
   * trigger fallback is gated on being an entry node so a mid-graph bucket whose
   * predecessor merely lacks a shape is never grafted onto the trigger's row.
   */
  private resolveItemShape(
    context: WorkflowNodeOutputShapeContext
  ): string | null {
    const fromPredecessor =
      context.resolvePredecessorOutputShape?.(this.id) ?? null;
    if (fromPredecessor !== null) {
      return fromPredecessor;
    }
    if (context.hasGraphPredecessor?.(this.id) === true) {
      return null;
    }
    const triggerSample = context.resolveUpstreamSample?.(
      WORKFLOW_TRIGGER_UPSTREAM_SOURCE_ID
    );
    return inferShapeFromSample(triggerSample ?? null);
  }
}
